class WarrantyForm extends HTMLElement {
  constructor() {
    super();
    const FORM_URL = "https://bunker-pimberly.quickfire-digital.workers.dev";
    this.abortController = new AbortController();
    this.lookupControllers = new Map();
    this.lookupTimeouts = new Map();
    this.isCaptchaPending = false;
    this.isSubmitting = false;
    this.captchaTimeoutId = null;
    this.nativeSubmit = null;
    this.successMessage = null;
    this.errorMessage = null;
    this.formUrl = FORM_URL;
    this.productIndex = 1;
  }

  connectedCallback() {
    if (this.dataset.initialized) {
      return;
    }

    this.dataset.initialized = "true";
    this.form = this.querySelector("form");
    this.addProductButton = this.querySelector("[data-warranty-add-product]");
    this.submitButton = this.form?.querySelector("[type='submit']");
    this.productsContainer = this.querySelector("[data-warranty-products]");
    this.productTemplate = this.querySelector("[data-warranty-product-template]");
    this.maxProducts = Number.parseInt(this.dataset.maxProducts, 10);
    this.successMessage = this.querySelector("[data-warranty-success]");
    this.errorMessage = this.querySelector("[data-warranty-error]");
    this.productIndex = this.getProductRowCount();

    if (Number.isNaN(this.maxProducts)) {
      this.maxProducts = Infinity;
    }

    this.bindEvents();
    this.updateAddButtonState();
    this.suppressNativeSubmit();
    this.primeCaptchaToken();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.lookupControllers.forEach((controller) => controller.abort());
    this.lookupControllers.clear();
    this.lookupTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.lookupTimeouts.clear();
    if (this.captchaTimeoutId) {
      window.clearTimeout(this.captchaTimeoutId);
      this.captchaTimeoutId = null;
    }
    this.restoreNativeSubmit();
  }

  bindEvents() {
    if (this.addProductButton) {
      this.addProductButton.addEventListener(
        "click",
        () => {
          this.addProductRow();
        },
        { signal: this.abortController.signal },
      );
    }

    if (this.form) {
      this.form.addEventListener(
        "submit",
        (event) => {
          event.preventDefault();
          this.submitForm();
        },
        { signal: this.abortController.signal },
      );
    }

    if (this.productsContainer) {
      this.productsContainer.addEventListener(
        "input",
        (event) => {
          const stockInput = event.target?.closest?.("[data-warranty-stock]");
          if (!stockInput) {
            return;
          }

          this.queueLookup(stockInput);
        },
        { signal: this.abortController.signal },
      );

      this.productsContainer.addEventListener(
        "click",
        (event) => {
          const removeButton = event.target?.closest?.("[data-warranty-remove-product]");
          if (!removeButton) {
            return;
          }

          const row = removeButton.closest("[data-warranty-product-row]");
          if (!row) {
            return;
          }

          this.removeProductRow(row);
        },
        { signal: this.abortController.signal },
      );
    }
  }

  addProductRow() {
    if (!this.productsContainer || !this.productTemplate) {
      return;
    }

    const currentCount = this.getProductRowCount();
    if (currentCount >= this.maxProducts) {
      return;
    }

    this.productIndex += 1;
    const nextIndex = this.productIndex;
    const templateMarkup = this.productTemplate.innerHTML.replace(/__index__/g, nextIndex);
    const wrapper = document.createElement("div");
    wrapper.innerHTML = templateMarkup.trim();

    const newRow = wrapper.firstElementChild;
    if (newRow) {
      this.productsContainer.appendChild(newRow);
    }

    this.updateAddButtonState();
  }

  removeProductRow(row) {
    if (!this.productsContainer || !row) {
      return;
    }

    const rows = this.productsContainer.querySelectorAll("[data-warranty-product-row]");
    if (rows.length <= 1) {
      return;
    }

    const stockInputs = row.querySelectorAll("[data-warranty-stock]");
    stockInputs.forEach((stockInput) => this.clearLookup(stockInput));
    row.remove();
    this.updateAddButtonState();
  }

  getProductRowCount() {
    return this.querySelectorAll("[data-warranty-product-row]").length;
  }

  updateAddButtonState() {
    if (!this.addProductButton) {
      return;
    }

    const currentCount = this.getProductRowCount();
    const isDisabled = currentCount >= this.maxProducts;

    this.addProductButton.disabled = isDisabled;
    this.addProductButton.classList.toggle("disabled", isDisabled);
  }

  queueLookup(stockInput) {
    let stockNumber = stockInput.value.trim();
    if (stockNumber.length > 5) {
      stockNumber = stockNumber.slice(0, 5);
      stockInput.value = stockNumber;
    }

    const row = stockInput.closest("[data-warranty-product-row]");
    const nameInput = row?.querySelector("[data-warranty-name]");
    const messageEl = row?.querySelector("[data-warranty-message]");

    if (!nameInput) {
      return;
    }

    if (messageEl) {
      messageEl.textContent = "";
    }

    if (!stockNumber || stockNumber.length < 5) {
      nameInput.value = "";
      this.clearLookup(stockInput);
      return;
    }

    this.clearLookup(stockInput);

    const timeoutId = window.setTimeout(() => {
      this.lookupProduct(stockNumber, nameInput, stockInput, messageEl);
    }, 400);

    this.lookupTimeouts.set(stockInput, timeoutId);
  }

  clearLookup(stockInput) {
    const existingTimeout = this.lookupTimeouts.get(stockInput);
    if (existingTimeout) {
      window.clearTimeout(existingTimeout);
      this.lookupTimeouts.delete(stockInput);
    }

    const controller = this.lookupControllers.get(stockInput);
    if (controller) {
      controller.abort();
      this.lookupControllers.delete(stockInput);
    }
  }

  async lookupProduct(stockNumber, nameInput, stockInput, messageEl) {
    const controller = new AbortController();
    this.lookupControllers.set(stockInput, controller);

    try {
      const response = await fetch(`${this.formUrl}/products/${encodeURIComponent(stockNumber)}`, {
        method: "GET",
        redirect: "follow",
        signal: controller.signal,
      });

      if (!response.ok) {
        nameInput.value = "";
        if (messageEl) {
          messageEl.textContent = this.getProductNotFoundMessage();
        }
        return;
      }

      const result = await response.json();
      if (result?.title) {
        nameInput.value = result.title;
        if (messageEl) {
          messageEl.textContent = "";
        }
      } else {
        nameInput.value = "";
        if (messageEl) {
          messageEl.textContent = this.getProductNotFoundMessage();
        }
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        nameInput.value = "";
        if (messageEl) {
          messageEl.textContent = this.getProductNotFoundMessage();
        }
      }
    } finally {
      this.lookupControllers.delete(stockInput);
    }
  }

  getProductNotFoundMessage() {
    return this.dataset.productNotFoundMessage || "Product Not Found";
  }

  async submitForm() {
    if (!this.form) {
      return;
    }

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.clearSuccessMessage();
    this.clearErrorMessage();
    this.toggleSubmitState(true);

    const token = await this.ensureCaptchaToken();
    if (this.shouldRunCaptchaProtection() && !token) {
      this.showErrorMessage();
      this.toggleSubmitState(false);
      return;
    }

    await this.submitFormData(token);
  }

  shouldRunCaptchaProtection() {
    return (
      this.form?.hasAttribute("data-shopify-captcha") &&
      typeof window !== "undefined" &&
      typeof window.Shopify?.captcha?.protect === "function"
    );
  }

  primeCaptchaToken() {
    if (!this.form || this.isCaptchaPending || !this.shouldRunCaptchaProtection()) {
      return;
    }

    this.isCaptchaPending = true;

    try {
      window.Shopify.captcha.protect(this.form, async () => {
        await this.waitForCaptchaToken();
        this.isCaptchaPending = false;
      });
    } catch (error) {
      this.isCaptchaPending = false;
    }
  }

  getCaptchaToken() {
    if (!this.form) {
      return "";
    }

    const input = this.form.querySelector(
      '[name="h-captcha-response"], [name="g-recaptcha-response"]',
    );
    return input?.value?.trim() || "";
  }

  async waitForCaptchaToken() {
    const existingToken = this.getCaptchaToken();
    if (existingToken) {
      return existingToken;
    }

    const start = Date.now();
    const timeoutMs = 5000;

    return new Promise((resolve) => {
      const check = () => {
        const token = this.getCaptchaToken();
        if (token) {
          resolve(token);
          return;
        }

        if (Date.now() - start >= timeoutMs) {
          resolve("");
          return;
        }

        this.captchaTimeoutId = window.setTimeout(check, 120);
      };

      check();
    });
  }

  async ensureCaptchaToken() {
    if (!this.shouldRunCaptchaProtection()) {
      return "";
    }

    const existingToken = this.getCaptchaToken();
    if (existingToken) {
      return existingToken;
    }

    if (!this.isCaptchaPending) {
      this.primeCaptchaToken();
    }

    const token = await this.waitForCaptchaToken();
    this.isCaptchaPending = false;
    return token;
  }

  async submitFormData(tokenOverride = "") {
    if (!this.form || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const formData = new FormData(this.form);
    this.toggleSubmitState(true);
    const token = tokenOverride || this.getCaptchaToken();
    formData.set("h-captcha-response", token);

    try {
      const response = await fetch(`${this.formUrl}/warranty-forms`, {
        method: "POST",
        body: formData,
        headers: { "hcaptcha-token": token },
      });

      if (!response.ok) {
        throw new Error("Warranty form submission failed");
      }

      this.form.reset();
      this.resetProductRows();
      this.showSuccessMessage();
    } catch (error) {
      this.showErrorMessage();
    } finally {
      this.isSubmitting = false;
      this.toggleSubmitState(false);
    }
  }

  suppressNativeSubmit() {
    if (!this.form || this.nativeSubmit) {
      return;
    }

    this.nativeSubmit = this.form.submit.bind(this.form);
    this.form.submit = () => {};
  }

  restoreNativeSubmit() {
    if (!this.form || !this.nativeSubmit) {
      return;
    }

    this.form.submit = this.nativeSubmit;
    this.nativeSubmit = null;
  }

  toggleSubmitState(isSubmitting) {
    if (!this.submitButton) {
      return;
    }

    this.submitButton.disabled = isSubmitting;
    this.submitButton.classList.toggle("disabled", isSubmitting);
  }

  clearSuccessMessage() {
    if (!this.successMessage) {
      return;
    }

    this.successMessage.classList.add("hidden");
  }

  clearErrorMessage() {
    if (!this.errorMessage) {
      return;
    }

    this.errorMessage.classList.add("hidden");
  }

  showSuccessMessage() {
    if (!this.successMessage) {
      return;
    }

    this.successMessage.classList.remove("hidden");
  }

  showErrorMessage() {
    if (!this.errorMessage) {
      return;
    }

    this.errorMessage.classList.remove("hidden");
  }

  resetProductRows() {
    if (!this.productsContainer) {
      return;
    }

    const rows = Array.from(this.productsContainer.querySelectorAll("[data-warranty-product-row]"));
    rows.slice(1).forEach((row) => this.removeProductRow(row));

    const firstRow = rows[0];
    if (!firstRow) {
      return;
    }

    const firstStock = firstRow.querySelector("[data-warranty-stock]");
    const firstName = firstRow.querySelector("[data-warranty-name]");
    const firstMessage = firstRow.querySelector("[data-warranty-message]");

    if (firstStock) firstStock.value = "";
    if (firstName) firstName.value = "";
    if (firstMessage) firstMessage.textContent = "";

    this.updateAddButtonState();
  }

  static register() {
    if (!customElements.get("warranty-form")) {
      customElements.define("warranty-form", WarrantyForm);
    }
  }
}

WarrantyForm.register();

export default WarrantyForm;
